// NEW: src/sounds/sound-manager.js
//
// Singleton sound manager. All UI audio goes through here so one mute toggle
// controls everything.
//
// Autoplay-safe: every play() is wrapped in a .catch so browsers that block
// audio before first user interaction fail silently. Since every sound in
// this app is fired in response to a tap, this rarely triggers in practice.
//
// Silent-mode-safe: we use plain HTMLAudioElement (not Web Audio API) so
// Android WebView and iOS both respect the device silent switch.
//
// Missing-file-safe: if a sound file fails to load (404, decode error, etc.)
// the audio element simply won't play — no crash, no noisy logs.

const STORAGE_KEY = "samajh_sound_muted";

class SoundManager {
  constructor() {
    this.audioPool = {};
    this.muted = this._loadMutePreference();
    this._preloaded = false;
  }

  /**
   * Preload all sounds. Call once on app boot. Idempotent — calling more than
   * once is a no-op (we don't re-create Audio elements).
   * @param {Record<string, string>} soundMap - { key: "/path/to/file.mp3" }
   */
  preload(soundMap) {
    if (this._preloaded) return;
    this._preloaded = true;
    for (const [key, src] of Object.entries(soundMap || {})) {
      try {
        const a = new Audio(src);
        a.preload = "auto";
        // Low volume by default so short UI sounds don't startle. Individual
        // callers can override via setVolume(key, v) if needed.
        a.volume = 0.6;
        // Silence network errors — a missing file shouldn't spam the console.
        a.addEventListener("error", () => {}, { once: true });
        this.audioPool[key] = a;
      } catch {
        // ignore — malformed URL, etc.
      }
    }
  }

  /**
   * Play a sound by key. Silently no-ops if muted, missing, or blocked.
   */
  play(key) {
    if (this.muted) return;
    const audio = this.audioPool[key];
    if (!audio) return;
    try {
      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      // ignore
    }
  }

  setMuted(muted) {
    this.muted = !!muted;
    try {
      localStorage.setItem(STORAGE_KEY, this.muted ? "1" : "0");
    } catch {
      // private-mode safari etc — memory state is enough
    }
  }

  isMuted() {
    return this.muted;
  }

  setVolume(key, v) {
    const a = this.audioPool[key];
    if (a) a.volume = Math.max(0, Math.min(1, v));
  }

  _loadMutePreference() {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }
}

const soundManager = new SoundManager();
export default soundManager;
